// A skill's files as a tree: folders indent their files, the selected file is highlighted, and a
// field at the bottom adds a file by path (`references/checklist.md` makes the folder).
import { FileText, Folder, Plus } from "@phosphor-icons/react";
import { type FormEvent, useState } from "react";
import { Button, Input } from "rizzui";

import { useT } from "../i18n";
import { cn } from "../ui/cn";
import type { EditableFile } from "./api";
import { tree } from "./files";

interface Props {
  files: EditableFile[];
  selected: string;
  onSelect: (path: string) => void;
  /** Returns the reason the path was refused, or undefined when added. */
  onAdd: (path: string) => string | undefined;
}

export function FileTree({ files, selected, onSelect, onAdd }: Props) {
  const t = useT().skills.files;
  const [path, setPath] = useState("");
  const [error, setError] = useState<string>();

  function add(e: FormEvent) {
    e.preventDefault();
    const problem = onAdd(path.trim());
    setError(problem);
    if (!problem) setPath("");
  }

  return (
    <div className="space-y-3">
      <ul className="m-0 list-none space-y-0.5 p-0" aria-label={t.title}>
        {tree(files).map((row) => (
          <li key={`${row.kind}:${row.path}`} style={{ paddingInlineStart: `${row.depth * 14}px` }}>
            {row.kind === "dir" ? (
              <span className="flex items-center gap-1.5 px-2 py-1 text-[13px] text-gray-500">
                <Folder weight="duotone" className="h-4 w-4" /> {row.name}/
              </span>
            ) : (
              <button
                type="button"
                aria-current={row.path === selected ? "true" : undefined}
                onClick={() => onSelect(row.path)}
                className={cn(
                  "flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-start font-mono text-[12.5px]",
                  row.path === selected ? "bg-primary-lighter/60 text-primary-dark" : "text-gray-700 hover:bg-gray-100",
                )}
              >
                <FileText weight="duotone" className="h-4 w-4 shrink-0" /> <span className="truncate">{row.name}</span>
              </button>
            )}
          </li>
        ))}
      </ul>
      <form className="flex items-start gap-2" onSubmit={add} aria-label={t.add}>
        <Input
          className="min-w-0 flex-1"
          size="sm"
          aria-label={t.newPath}
          placeholder="references/notes.md"
          inputClassName="font-mono"
          value={path}
          error={error}
          onChange={(e) => setPath(e.target.value)}
        />
        <Button type="submit" size="sm" variant="outline" className="gap-1" disabled={path.trim() === ""}>
          <Plus weight="bold" className="h-3.5 w-3.5" /> {t.addButton}
        </Button>
      </form>
    </div>
  );
}
